<template>
  <div class="ui-product">
    <header class="ui-product__header">
      <CRow class="justify-content-between align-items-center h-100">
        <CCol sm="auto" class="mt-2 mb-1 my-sm-0">
          <div class="ui-type-display-lg">
            {{ $t("producer.products.priceEdit.title") }}
          </div>
        </CCol>
        <CCol sm="auto" class="mt-1 mb-2 my-sm-0">
          <CButton color="outline-primary" class="mr-3" @click="onCancel()">{{
            $t("producer.products.priceEdit.btnCancel")
          }}</CButton>
          <CButton
            color="outline-primary"
            class="mr-3"
            @click="onSave(false)"
            >{{ $t("producer.products.priceEdit.btnSave") }}</CButton
          >
          <CButton color="primary" @click="onSave(true)">{{
            $t("producer.products.priceEdit.btnSaveContinue")
          }}</CButton>
        </CCol>
      </CRow>
    </header>
    <section class="ui-product__body">
      <div class="d-flex justify-content-center" v-if="loading">
        <CSpinner color="info" />
      </div>

      <div class="ui-product__main ui-product__main--full" v-if="initialized">
        <CCard class="card-bordered">
          <CCardBody>
            <CRow class="align-items-end">
              <CCol md="5">
                <CInput
                  :label="$t('producer.products.priceEdit.search.title')"
                  :placeholder="
                    $t('producer.products.priceEdit.search.placeholder')
                  "
                  v-model="search"
                >
                  <template #prepend-content>
                    <font-awesome-icon icon="search" />
                  </template>
                </CInput>
              </CCol>
            </CRow>

            <div class="table-responsive">
              <table class="table">
                <thead>
                  <tr>
                    <th></th>
                    <th>{{ $t("producer.products.priceEdit.table.name") }}</th>
                    <th>
                      {{ $t("producer.products.priceEdit.table.price") }}
                      <em>*</em>
                    </th>
                    <th>
                      {{ $t("producer.products.priceEdit.table.priceSugg") }}
                    </th>
                    <th>
                      {{ $t("producer.products.priceEdit.table.tax") }}
                      <em>*</em>
                    </th>
                    <th>
                      {{ $t("producer.products.priceEdit.table.market") }}
                      <em>*</em>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="variant in searchResult" :key="variant.id">
                    <td class="ui-cell-switch">
                      <CSwitch
                        variant="3d"
                        size="sm"
                        color="secondary"
                        :checked.sync="variant.is_active"
                      />
                    </td>
                    <td>
                      <strong>{{ getProductName(variant) }}</strong>
                      <div>{{ getVariantName(variant) }}</div>
                    </td>
                    <td class="ui-cell-price">
                      <CInput
                        v-model="variant.price"
                        :append="pwSuffix(variant)"
                        :isValid="variant.isValid('price')"
                        :invalidFeedback="variant.getErrorMessage('price')"
                      />
                    </td>
                    <td class="ui-cell-price">
                      <CInput
                        v-model="variant.price_sugg"
                        :append="pwSuffix(variant)"
                        :isValid="variant.isValid('price_sugg')"
                        :invalidFeedback="variant.getErrorMessage('price_sugg')"
                      />
                    </td>
                    <td class="ui-cell-vat">
                      <CSelect
                        :value.sync="variant.tax_rate_id"
                        :options="options.tax"
                        :disabled="options.tax.length === 0"
                        :isValid="variant.isValid('tax_rate_id')"
                        :invalidFeedback="
                          variant.getErrorMessage('tax_rate_id')
                        "
                      />
                    </td>
                    <td class="ui-cell-vat">
                      <CSelect
                        :value.sync="variant.for_market"
                        :options="options.markets"
                        :disabled="options.markets.length === 0"
                        :isValid="variant.isValid('for_market')"
                        :invalidFeedback="variant.getErrorMessage('for_market')"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CCardBody>
        </CCard>
      </div>
    </section>
  </div>
</template>

<script>
import get from "lodash/get";
import toLower from "lodash/toLower";
import pick from "lodash/pick";
import { getOptionLabel } from "../../../helpers/common";
import {
  getTaxIdOptions,
  getPriceUnitOptions,
  getPwOptions,
  getProducerMarkets,
} from "../../../helpers/options";
import { endPoint } from "../../../ds";
import ProductVariant from "../../../helpers/ProductVariant";

const restoreVariants = (variants) => {
  return variants.map((variantData) => {
    //console.log("for_market", variantData);
    return new ProductVariant(variantData);
  });
};

export default {
  name: "ProductsTableListEditPrices",

  async mounted() {
    if (this.variants.length < 1) {
      this.onCancel();
    }
    await this.init();
  },

  beforeRouteLeave(to, from, next) {
    this.$store.commit("service/selectedVariants", []);
    next();
  },

  data: function () {
    return {
      initialized: false,
      loading: false,
      search: "",
      variants: restoreVariants(this.$store.state.service.variants),
      options: {
        tax: [],
        price_unit: getPriceUnitOptions(this.$i18n),
        pw: getPwOptions(this.$i18n),
        markets: [],
      },
    };
  },
  computed: {
    lang: function () {
      return this.$i18n.locale;
    },
    currencySymbol: function () {
      return get(
        this.$store.state,
        "connections.current.family.wallets[0].get_currency_display_short"
      );
    },
    searchResult: function () {
      let results = this.variants;
      if (this.search !== "" && this.search) {
        const lang = this.lang;
        const search = toLower(this.search);
        results = results.filter((item) => {
          const productName = toLower(
            get(item, `productproducer.translations.${lang}.name`)
          );
          const variantName = toLower(
            get(item, `productproducer.translations.${lang}.name`)
          );

          return productName.includes(search) || variantName.includes(search);
        });
      }
      return results;
    },
  },

  methods: {
    async init() {
      this.loading = true;
      const producer_id = this.$store.state.role.producer__id;
      try {
        this.options.tax = await getTaxIdOptions({
          country_id: this.$store.state.role.country_id,
        });
        this.options.markets = await getProducerMarkets({ producer_id }, this);
        this.initialized = true;
      } catch (e) {
        console.error(e);
      }
      this.loading = false;
    },

    pwSuffix(variant) {
      if (variant.productproducer.pw_option === 3) {
        return `${this.currencySymbol} / ${
          variant.productproducer.pw_len
        } ${getOptionLabel(
          variant.productproducer.pw_unit,
          this.options.price_unit
        )}`;
      } else {
        return `${this.currencySymbol} / ${
          variant.productproducer.pw_len
        } ${getOptionLabel(
          variant.productproducer.pw_option,
          this.options.pw,
          "suffix"
        )}`;
      }
    },

    getProductName(variant) {
      return get(variant, `productproducer.translations.${this.lang}.name`);
    },

    getVariantName(variant) {
      return get(variant, `translations.${this.lang}.name`);
    },

    onCancel() {
      this.$router.push({
        name: "RoleProductsList",
        params: { id: this.$store.state.role.id },
      });
    },

    onSave(continueEdit = true) {
      const self = this;
      const roleId = this.$store.state.role.id;
      const url =
        endPoint("role_producer_products_lots_NEW", [roleId]) + "multi_update/";

      this.variants.forEach((variant) => {
        variant.resetErrors();
      });

      const params = this.variants.map((variant) => {
        const data = variant.prepare(true);
        return pick(data, [
          "id",
          "is_active",
          "price",
          "price_sugg",
          "tax_rate_id",
        ]);
      });

      const handleRequestErrors = (error) => {
        const { fields } = error.response.data.errors;
        if (fields) {
          self.variants.forEach((variant, idx) => {
            const variantErrors = fields[idx];
            if (variantErrors !== undefined) {
              variant.setErrors(variantErrors);
            }
          });
        }
      };

      this.axios
        .patch(url, params)
        .then(() => {
          self.$store.dispatch("toaster/add", {
            title: "OK!",
            text: this.$t("role.products.massEdit.price.toast.success"),
            autohide: true,
          });
          if (!continueEdit) {
            self.onCancel();
          }
        })
        .catch(handleRequestErrors);
    },
  },
};
</script>
